import { makeAutoObservable, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { AxiosError } from 'axios'
import { EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import {
  ICountriesEnum,
  type IParamsUpdateTollFreeBusinessInfo,
  type IResponseEinItem,
} from 'entities/Compliance/api/types/complianceType'
import { ComplianceApi } from 'entities/Compliance'
import { ReachableUrlStore } from 'entities/Compliance/ui/ReachableUrl'
import { EinItem } from 'pages/settings/pages/compliance/ui/EinItem/EinItem'
import type { TollFreeStepsStore } from 'pages/settings/pages/compliance/pages/tollFree/store/tollFreeStepsStore'
import { BusinessAddressFormStore } from 'pages/settings/ui/BusinessAddressForm'
import {
  type RegistrationAuthorityConfig,
  RegistrationAuthoritySelectStore,
} from 'pages/settings/pages/compliance/ui/RegistrationAuthoritySelect'
import { IRSEINDocumentUploaderStore } from 'pages/settings/pages/compliance/ui/IRSEINDocumentUploader'
import styles from './styles.module.scss'

const tollFreeRegistrationAuthorityConfig: RegistrationAuthorityConfig = {
  countries: [
    {
      label: 'US: Employer Identification Number (EIN)',
      id: ICountriesEnum.US,
      iconL: 'flagUSA',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'CA: Canadian Business Number (CBN)',
      id: ICountriesEnum.CA,
      iconL: 'flagCanada',
      variant: EnumDropdownItemVariant.CheckedRight,
      divider: true,
    },
    {
      label: 'I don’t have a business Tax ID',
      id: ICountriesEnum.OTHER,
      iconL: 'slashCircle',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
  ],
}

export class BusinessInfoStore {
  private _generalInfoActiveTab = 1
  private _errorMessage = ''
  private _einItem: IResponseEinItem | null = null
  private _einItems: IResponseEinItem[] = []
  private _einSearch = ''
  private _maxEinLength = 9
  private _websiteUrl: string | null = null
  private _socialMediaUrl: string | null = null
  private _noWebsiteUrl = false
  private _einErrorMessage = ''
  private readonly _debouncedFetchEinData: (search: string) => void
  private readonly _addressFormStore: BusinessAddressFormStore
  private readonly _registrationAuthorityStore: RegistrationAuthoritySelectStore
  private readonly _irsEINDocumentUploaderStore: IRSEINDocumentUploaderStore

  readonly reachableWebsiteUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })
  readonly reachableSocialUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })

  constructor(private _tollFreeStepsStore: TollFreeStepsStore) {
    this._registrationAuthorityStore = new RegistrationAuthoritySelectStore(
      tollFreeRegistrationAuthorityConfig
    )
    this._irsEINDocumentUploaderStore = new IRSEINDocumentUploaderStore()

    makeAutoObservable(this)
    this._debouncedFetchEinData = debounce(this.fetchEinData, 300)
    this._addressFormStore = new BusinessAddressFormStore({
      deleteErrorValue: this.deleteErrorValue,
      onFieldChange: this.handleAddressFieldChange,
    })
  }

  init = async () => {
    await this._addressFormStore.init()
  }

  get registrationAuthorityStore() {
    return this._registrationAuthorityStore
  }

  get irsEINDocumentUploaderStore() {
    return this._irsEINDocumentUploaderStore
  }

  get tollFreeStepsStore() {
    return this._tollFreeStepsStore
  }

  get addressFormStore() {
    return this._addressFormStore
  }

  get einItem() {
    return this._einItem
  }

  get errorMessage() {
    return this._errorMessage
  }

  get generalInfoActiveTab() {
    return this._generalInfoActiveTab
  }

  get einItemList(): IDropdownItem[] {
    if (this._einItems.length) {
      return [
        {
          id: 0,
          label: this._einItems[0].name,
          className: styles.einItem,
          variant: EnumDropdownItemVariant.Custom,
          renderOption: () => <EinItem einItem={this._einItems[0]} />,
        },
      ]
    }
    return []
  }

  get einSearch() {
    if (this._einSearch.length <= 2) {
      return this._einSearch
    }

    if (this.registrationAuthorityStore.isUSLocation) {
      return `${this._einSearch.slice(0, 2)}-${this._einSearch.slice(2)}`
    }

    return this._einSearch
  }

  get einFormatted() {
    const ein = this._einSearch.replace(/[^0-9]/g, '')

    if (this.registrationAuthorityStore.isUSLocation) {
      return ein.length <= 2 ? this._einSearch : `${ein.slice(0, 2)}-${ein.slice(2)}`
    }

    return ein
  }

  get einLength() {
    if (
      this.registrationAuthorityStore.isUSLocation ||
      this.registrationAuthorityStore.isCALocation
    ) {
      return 9
    }
    return null
  }

  get einPlaceholder() {
    if (this.registrationAuthorityStore.isUSLocation) return 'e.g. 12-3456789'
    if (this.registrationAuthorityStore.isCALocation) return 'e.g. 123456789'
    return 'e.g. 123456789'
  }

  get einErrorMessage() {
    return this._einErrorMessage
  }

  get einErrorMessageText() {
    return `Must be a ${this.einLength}-digit number ${this.einPlaceholder}`
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get socialMediaUrl() {
    return this._socialMediaUrl
  }

  get noWebsiteUrl() {
    return this._noWebsiteUrl
  }

  get countries() {
    return this._addressFormStore.countries
  }

  get states() {
    return this._addressFormStore.states
  }

  get activeCountryId() {
    return this._addressFormStore.activeCountryId
  }

  get activeCountryInfo() {
    return this._addressFormStore.activeCountryInfo
  }

  get zipCode() {
    return this._addressFormStore.zipCode
  }

  get isWebsiteNeedFilled() {
    const socialMediaUrlRequired = this._noWebsiteUrl && !this._socialMediaUrl
    const noRequiredWebsiteUrl = !this._noWebsiteUrl && !this._websiteUrl

    return (
      this.reachableWebsiteUrlStore.isError ||
      this.reachableSocialUrlStore.isError ||
      socialMediaUrlRequired ||
      noRequiredWebsiteUrl
    )
  }
  private _isUrlValidateLoading = false
  private _isNeedValidateUrl = false
  get isNextDisabled() {
    if (this._isNeedValidateUrl) return true
    if (this._isUrlValidateLoading) return true
    if (this.generalInfoActiveTab === 2 || this.registrationAuthorityStore.isCALocation) {
      return !this.isManuallyItemFulled
    }
    if (this.generalInfoActiveTab === 1) {
      return this._einSearch.length !== 9 || this.isWebsiteNeedFilled
    }

    return false
  }

  get isManuallyItemFulled() {
    const keys = ['city', 'name', 'state', 'street1'] as const

    if (!this._addressFormStore.isZipFilled || this._errorMessage || this.isWebsiteNeedFilled)
      return false

    const isEinFilled = this._einSearch.length === 9

    return keys.every((key) => this._einItem?.[key]?.length) && isEinFilled
  }

  setGeneralInfoActiveTab = (value: number) => {
    this._generalInfoActiveTab = value
  }

  changeGeneralInfoTab = (value: number) => {
    const isEinNumberExist =
      this._tollFreeStepsStore.complianceLayerStore.businessInformation?.registration_number
    if (!isEinNumberExist && value === 1) {
      this.resetData()
    }
    this.setGeneralInfoActiveTab(value)
  }

  resetData = () => {
    this._einItems = []
    this._websiteUrl = null
    this._socialMediaUrl = null
    this._einItem = null
    this._einSearch = ''
    this._errorMessage = ''
    this._einErrorMessage = ''
    this.reachableSocialUrlStore.dispose()
    this.reachableWebsiteUrlStore.dispose()
    this._noWebsiteUrl = false
    this._generalInfoActiveTab = 1
    this._irsEINDocumentUploaderStore.reset()
    this._addressFormStore.setAddressData(null)
  }

  setEinItem = (value: IResponseEinItem | null) => {
    this._einItem = value
    if (value) {
      this._addressFormStore.setAddressData(value)
      const country = this.registrationAuthorityStore.country
      if (country === ICountriesEnum.US || country === ICountriesEnum.CA) {
        this._addressFormStore.setActiveCountry(country)
      }
    }
  }

  setFirstEinItem = () => {
    if (this._einItems.length) {
      this.setEinItem(this._einItems[0])
    }
  }

  setEinSearch = (search: string) => {
    const stringWithoutDashes = search.replace(/[^0-9]/g, '')

    const isUsaLocationAuto =
      this.generalInfoActiveTab === 1 && this.registrationAuthorityStore.isUSLocation
    const isNeedClear = stringWithoutDashes.length < this._maxEinLength && isUsaLocationAuto
    if (isNeedClear) {
      this._einItems = []
      this.setEinItem(null)
    }

    this.setErrorMessage('')
    this.setEinErrorMessage('')

    const isManualEntryFlow =
      this.generalInfoActiveTab === 2 || this.registrationAuthorityStore.isCALocation
    if (isManualEntryFlow && this.einLength && stringWithoutDashes.length > this.einLength) {
      this.setEinErrorMessage(this.einErrorMessageText)
      return
    }

    if (stringWithoutDashes.length > this._maxEinLength) return

    this._einSearch = stringWithoutDashes

    if (isManualEntryFlow) {
      if (!this._einItem) {
        this._einItem = this.createEmptyEinItem()
      }
      this._einItem.ein = stringWithoutDashes
    }

    if (this.isNeedCheckEin(stringWithoutDashes)) {
      this.checkEinNumber()
    }
  }

  setErrorMessage = (message: string) => {
    this._errorMessage = message
  }

  setEinErrorMessage = (message: string) => {
    this._einErrorMessage = message
  }

  checkEin = () => {
    const isManualEntryFlow =
      this.generalInfoActiveTab === 2 || this.registrationAuthorityStore.isCALocation
    if (!isManualEntryFlow || !this.einLength) return

    if (this._einSearch.length > 0 && this._einSearch.length < this.einLength) {
      this.setEinErrorMessage(this.einErrorMessageText)
    } else {
      this.setEinErrorMessage('')
    }
  }

  isNeedCheckEin = (stringWithoutDashes: string) => {
    const isUsaLocationAuto =
      this.generalInfoActiveTab === 1 && this.registrationAuthorityStore.isUSLocation

    return isUsaLocationAuto && stringWithoutDashes.length === this._maxEinLength
  }

  checkEinNumber = () => {
    if (this._einSearch.length && !this._einItem?.ein) this._debouncedFetchEinData(this._einSearch)
  }

  setWebsiteUrl = (url: string | null, fromRequest?: boolean) => {
    this._isNeedValidateUrl = !fromRequest
    this._tollFreeStepsStore?.deleteErrorValue('website_url')

    this.reachableWebsiteUrlStore.dispose()

    this._websiteUrl = url
  }

  handleSetNoWebsiteUrl = () => {
    this.reachableWebsiteUrlStore.dispose()
    this.reachableSocialUrlStore.dispose()

    this.setWebsiteUrl(null)
    this.setSocialMediaUrl(null)
    this.setNoWebsiteUrl(!this._noWebsiteUrl)
  }

  validateWebsiteUrl = async (url: string, type: 'websiteUrl' | 'socialMediaUrl') => {
    this._isUrlValidateLoading = true

    try {
      type === 'websiteUrl'
        ? await this.reachableWebsiteUrlStore.checkSingleUrl(url)
        : await this.reachableSocialUrlStore.checkSingleUrl(url)
    } finally {
      runInAction(() => {
        this._isUrlValidateLoading = false
        this._isNeedValidateUrl = false
      })
    }
  }

  setSocialMediaUrl = (url: string | null) => {
    this._isNeedValidateUrl = true
    this._tollFreeStepsStore?.deleteErrorValue('social_media_url')

    this.reachableSocialUrlStore.dispose()
    this._socialMediaUrl = url
  }

  setNoWebsiteUrl = (value: boolean) => {
    this._noWebsiteUrl = value
  }

  addBusinessNameManually = (name: string) => {
    this._tollFreeStepsStore?.deleteErrorValue('business_name')

    if (!this._einItem) {
      this._einItem = this.createEmptyEinItem()
    }
    this._einItem.name = name
  }

  setActiveCountry = (countryId: string) => {
    this._addressFormStore.setActiveCountry(countryId)
  }

  get legalBusinessNameHelperText() {
    if (this._registrationAuthorityStore.isUSLocation) {
      return 'Enter the exact legal business name, as registered with the EIN'
    }
    if (this._registrationAuthorityStore.isCALocation) {
      return 'Enter the exact legal business name, as registered with the CBN'
    }

    return ''
  }

  get registrationTypeId() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.US:
        return 2
      case ICountriesEnum.CA:
        return 1
      default:
        return 0
    }
  }

  setCountryFromRegistrationTypeId = (registrationTypeId: number | null | undefined) => {
    if (!registrationTypeId) return

    switch (registrationTypeId) {
      case 2:
        this.registrationAuthorityStore.setCountry(ICountriesEnum.US)
        break
      case 1:
        this.registrationAuthorityStore.setCountry(ICountriesEnum.CA)
        break
      default:
        this.registrationAuthorityStore.setCountry(ICountriesEnum.OTHER)
        break
    }
  }

  get errorStore() {
    return this.tollFreeStepsStore.complianceLayerStore.errorStore
  }

  fetchEinData = async (search: string) => {
    try {
      const { data } = await ComplianceApi.getEinData({ ein: search })

      this.addEin(data.data)
      this.setErrorMessage('')
    } catch (error) {
      if (error instanceof AxiosError) {
        this.setErrorMessage(error.errors.getError('message'))
      }
    }
  }

  addEin = (einList: IResponseEinItem[]) => {
    this._einItems = einList
  }

  nextStepClick = async () => {
    const { profileId } = this.tollFreeStepsStore

    if (!this._einItem || !profileId) return

    try {
      this.tollFreeStepsStore.setStepLoading(true)

      const info: IParamsUpdateTollFreeBusinessInfo = {
        registration_type_id: this.registrationTypeId,
        business_name: this._einItem?.name,
        ein: this._einItem?.ein,
        country: this.activeCountryId,
        city: this._einItem?.city,
        street_address_1: this._einItem?.street1,
        state_id: this._einItem?.state,
        website_url: this.websiteUrl || '',
        without_website: this.noWebsiteUrl,
        postal_code: this._einItem?.zip,
        should_update_stripe_customer: true,
        social_media_url: this._socialMediaUrl || null,
      }

      if (this._einItem?.street2?.length) {
        info.street_address_2 = this._einItem?.street2
      }

      const { data } = await ComplianceApi.updateTollFreeBusinessInfo(info, profileId)

      if (data) {
        uiStore.changeRoute({
          path: this._tollFreeStepsStore.tollFreeNavigationStore.getNextUrl,
        })
        this.tollFreeStepsStore.setDataFromTollFreeRequest(data)
      }
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        const tollFreeBusinessInfoFieldKeys = [
          'business_name',
          'ein',
          'country',
          'city',
          'street_address_1',
          'street_address_2',
          'state_id',
          'postal_code',
          'zip_code',
          'website_url',
          'social_media_url',
          'phone_number',
        ]
        this.errorStore?.prepareFieldsErrorAndSave(e.errors, tollFreeBusinessInfoFieldKeys)
      }
    } finally {
      this.tollFreeStepsStore.setStepLoading(false)
    }
  }

  private deleteErrorValue = (keyName: string) => {
    this._tollFreeStepsStore?.deleteErrorValue(keyName)
  }

  private createEmptyEinItem = (): IResponseEinItem => {
    return {
      city: '',
      ein: '',
      ein_formatted: '',
      name: '',
      state: '',
      street1: '',
      street2: '',
      zip: '',
    }
  }

  private handleAddressFieldChange = () => {
    this.syncAddressDataToEinItem()
  }

  private syncAddressDataToEinItem = () => {
    const addressData = this._addressFormStore.addressData
    if (!addressData) return

    if (!this._einItem) {
      this._einItem = this.createEmptyEinItem()
    }

    this._einItem.street1 = addressData.street1 || ''
    this._einItem.street2 = addressData.street2 || ''
    this._einItem.city = addressData.city || ''
    this._einItem.state = addressData.state || ''
    this._einItem.zip = addressData.zip || ''
  }
}
